import { useEffect, useState } from 'react';
import { useMutation } from 'react-apollo';
import produce from 'immer';
import { defaultDataIdFromObject } from 'apollo-cache-inmemory';
import omit from 'lodash.omit';
import pMap from 'p-map-browser';

import {
  cardElementConfigurationCreate as cardElementConfigurationCreateMutation,
  cardElementConfigurationsDelete as cardElementConfigurationsDeleteMutation,
  cardElementConfigurationUpdate as cardElementConfigurationUpdateMutation,
} from '../../../../apps/cards/graphql/mutations/cardElementConfiguration';
import accountCardDetails from '../../graphql/fragments/accountCardDetails';
import {
  getCardElementConfigurationInputByElement,
  getMatchingImageElements,
  getMatchingTextElements,
} from './helpers';
import {
  getAccountCardElementsConfiguration,
  isCardElementEditable,
} from '../../components/AccountCard/helpers';
import getLogger from '../../../../util/logger';
import { CARD_ELEMENT_TYPE, CARD_SIDE } from '../../constants';
import { TEXT_PRESETS } from '../../../../components/global/CardDesigner/components/CardDesignerCardElements/CardDesignerCardElementText/helpers';
import sleep from '../../../../util/sleep';

const log = getLogger('useCardElementConfiguration');

/**
 * CardElementConfiguration hook.
 *
 * @param {Object} options The options.
 * @param {Object} [options.accountCard] The AccountCard.
 * @param {Object} [options.presetCardElementConfigurationInputs] Preset element configurations to apply to the AccountCard immediately.
 * @param {Function} [options.onAfterElementConfigurationChange] Callback.
 * @param {Boolean} [options.linkTextElements] Whether or not text elements should be matched to other similar elements and updated.
 * @param {Boolean} [options.linkImageElements] Whether or not image elements (e.g., PHOTO) should be matched by width & height and updated together.
 * @returns {{onElementConfigurationChange: (function(*=, *=): Promise<void>), cardElementLoading: unknown}}
 */
const useCardElementConfiguration = ({
  accountCard,
  presetCardElementConfigurationInputs,
  onAfterElementConfigurationChange,
  linkTextElements,
  linkImageElements,
}) => {
  const [cardElementLoading, setCardElementLoading] = useState(null);
  // const [cardElementSavedConfigurations, setCardElementSavedConfigurations] = useState({});
  const [cardElementConfigurationCreate] = useMutation(
    cardElementConfigurationCreateMutation,
  );
  const [cardElementConfigurationUpdate] = useMutation(
    cardElementConfigurationUpdateMutation,
  );
  const [cardElementConfigurationsDelete] = useMutation(
    cardElementConfigurationsDeleteMutation,
  );

  const onElementConfigurationChange = async (element, config) => {
    if (!element) {
      return;
    }

    const onCreateOrUpdateCardElementConfiguration = async (
      { cardElement, cardElementConfiguration, side },
      ignoreLoadingState = false,
    ) => {
      const accountCardSide = accountCard.account_card_sides.find(
        x => x.side === side,
      );
      const accountCardSideCardElementConfigurationExists = accountCardSide.card_element_configurations.find(
        x => {
          return x.card_element?.id === cardElement.id;
        },
      );

      if (!ignoreLoadingState) {
        setCardElementLoading(cardElement);
      }

      if (accountCardSideCardElementConfigurationExists) {
        // Update the CardElementConfiguration if it exists.
        const response = await cardElementConfigurationUpdate({
          variables: {
            input: {
              card_element_configuration_id: cardElementConfiguration.id,
              ...getCardElementConfigurationInputByElement(element, config),
            },
          },
        });

        if (!ignoreLoadingState) {
          setCardElementLoading(null);
        }
        onAfterElementConfigurationChange?.(
          response.data.cardElementConfigurationUpdate
            .card_element_configuration,
        );

        return;
      }

      try {
        // Create the CardElementConfiguration for this AccountCardSide if it does not exist.
        const { box_shadow: boxShadow, text_shadow: textShadow, opacity } =
          element?.defaultCardElementConfiguration ?? {};
        const response = await cardElementConfigurationCreate({
          variables: {
            input: {
              card_element_id: cardElement.id,
              account_card_side_id: accountCardSide.id,
              // Box shadows would've been added to designs by PROs rather than the user generating them.
              // Therefore, rely on the defaultCardElementConfiguration to determine whether or not to add them
              // for the create element configuration call.
              ...getCardElementConfigurationInputByElement(element, {
                opacity,
                box_shadow: boxShadow
                  ? omit(boxShadow, '__typename')
                  : undefined,
                text_shadow: textShadow
                  ? omit(textShadow, '__typename')
                  : undefined,
                ...config,
              }),
            },
          },
          update: (cache, { data }) => {
            const cachedAccountCard = cache.readFragment({
              id: defaultDataIdFromObject(accountCard),
              fragment: accountCardDetails,
              fragmentName: 'accountCardDetails',
            });

            const newCardElementConfiguration =
              data.cardElementConfigurationCreate.card_element_configuration;

            const updatedAccountCard = produce(cachedAccountCard, draft => {
              draft.account_card_sides
                .find(x => x.id === accountCardSide.id)
                .card_element_configurations.push(newCardElementConfiguration);
            });

            cache.writeFragment({
              id: defaultDataIdFromObject(accountCard),
              fragment: accountCardDetails,
              fragmentName: 'accountCardDetails',
              data: updatedAccountCard,
            });
          },
        });

        if (!ignoreLoadingState) {
          setCardElementLoading(null);
        }
        onAfterElementConfigurationChange?.(
          response.data.cardElementConfigurationCreate
            .card_element_configuration,
        );
      } catch (error) {
        onAfterElementConfigurationChange(error);
      }
    };

    if (
      linkTextElements &&
      element?.cardElement?.type === CARD_ELEMENT_TYPE.TEXT &&
      !Object.values(TEXT_PRESETS).includes(
        element?.cardElementConfiguration?.help_placeholder,
      )
    ) {
      const elements = getMatchingTextElements({ element, accountCard });
      await pMap(
        elements,
        async elem => {
          await onCreateOrUpdateCardElementConfiguration(elem);
        },
        { concurrency: 3 },
      );
    } else if (
      linkImageElements &&
      element?.cardElement?.type === CARD_ELEMENT_TYPE.PHOTO
    ) {
      const elements = getMatchingImageElements({ element, accountCard });
      setCardElementLoading(element?.cardElement);
      await pMap(
        elements,
        async elem => {
          await onCreateOrUpdateCardElementConfiguration(elem, true);
        },
        { concurrency: 3 },
      );
      await sleep(250);
      setCardElementLoading(null);
    } else {
      await onCreateOrUpdateCardElementConfiguration(element);
    }
  };

  const onCardElementConfigurationDelete = async element => {
    const { cardElementConfiguration, side } = element;
    const accountCardSide = accountCard.account_card_sides.find(
      x => x.side === side,
    );

    try {
      await cardElementConfigurationsDelete({
        variables: {
          input: {
            card_element_configuration_ids: [cardElementConfiguration.id],
          },
        },
        update: cache => {
          const cachedAccountCard = cache.readFragment({
            id: defaultDataIdFromObject(accountCard),
            fragment: accountCardDetails,
            fragmentName: 'accountCardDetails',
          });

          const updatedAccountCard = produce(cachedAccountCard, draft => {
            draft.account_card_sides = draft.account_card_sides.map(
              accountCardSideRecord => {
                if (accountCardSideRecord.id !== accountCardSide.id) {
                  return accountCardSideRecord;
                }

                return {
                  ...accountCardSideRecord,
                  card_element_configurations: accountCardSideRecord.card_element_configurations.filter(
                    x => x.id !== cardElementConfiguration.id,
                  ),
                };
              },
            );
          });

          cache.writeFragment({
            id: defaultDataIdFromObject(accountCard),
            fragment: accountCardDetails,
            fragmentName: 'accountCardDetails',
            data: updatedAccountCard,
          });
        },
      });

      setCardElementLoading(null);
      onAfterElementConfigurationChange?.();
    } catch (error) {
      onAfterElementConfigurationChange(error);
    }
  };

  // If an AccountCard is passed "presetCardElementConfigurationInputs", i.e.
  // a set of CardElementConfigurationInputs to be applied immediately in the card
  // this hook here takes care of it. Think pre-setting a photo on a Photo CardElement
  // or pre-setting an address or bulk addresses on an Address CardElement.
  //
  // By default, for each preset CardElementConfigurationInput it applies it to the first editable CardElement
  // without existing CardElementConfiguration that has the same CardElementType.
  useEffect(() => {
    if (!presetCardElementConfigurationInputs?.length > 0 || !accountCard) {
      return;
    }

    // Get the flattened list of CardElement states for ease of use.
    const cardElementConfigurationStates = getAccountCardElementsConfiguration(
      accountCard,
    );

    presetCardElementConfigurationInputs.forEach(
      presetCardElementConfigurationInput => {
        // Find the first editable CardElement that matches
        // the card element type in the preset configuration and apply that
        // preset configuration. Focus primarily on the front side
        const sortedState = [...cardElementConfigurationStates].sort((a, b) => {
          if (a.cardElement.z_index < b.cardElement.z_index) {
            return -1;
          }
          if (a.cardElement.z_index > b.cardElement.z_index) {
            return 1;
          }
          return 0;
        });

        const frontElement = sortedState.find(cardElementConfigurationState => {
          return (
            cardElementConfigurationState.cardElement.type ===
              presetCardElementConfigurationInput.cardElement.type &&
            isCardElementEditable(cardElementConfigurationState) &&
            cardElementConfigurationState.side === CARD_SIDE.FRONT
          );
        });
        const backElement = sortedState.find(cardElementConfigurationState => {
          return (
            cardElementConfigurationState.cardElement.type ===
              presetCardElementConfigurationInput.cardElement.type &&
            isCardElementEditable(cardElementConfigurationState) &&
            cardElementConfigurationState.side === CARD_SIDE.BACK
          );
        });

        const element = frontElement ?? backElement;

        if (element) {
          // No need to await here, fire immediately and as fast as possible
          // for all elements.
          // TODO [cards] is this dangerous? What happens if this fails for an element?
          //  perhaps a Promise.all here with try and catch would work better.
          onElementConfigurationChange(element, {
            ...presetCardElementConfigurationInput.cardElementConfiguration,
            cropbox:
              presetCardElementConfigurationInput.cardElementConfiguration
                .cropbox ??
              presetCardElementConfigurationInput.calculateCropbox?.(
                element,
                accountCard.print_product_configuration?.print_product_size
                  ?.print_size,
              ),
          });
        }
      },
    );
  }, [accountCard?.id]);

  // Expose the CardElementConfiguration change API globally so that
  // native clients can pick it up.
  useEffect(() => {
    window.waldoApps = {
      ...window.waldoApps,
      onAccountCardElementConfigurationChange: (cardElementId, input) => {
        log.info(
          'onAccountCardElementConfigurationChange() :: received input:',
          cardElementId,
          input,
        );

        // Find the CardElement
        const elements = getAccountCardElementsConfiguration(accountCard);
        const element = elements.find(x => x.cardElement.id === cardElementId);

        if (!element) {
          log.warning(
            'onAccountCardElementConfigurationChange() :: No CardElement found.',
          );

          return;
        }

        // Update the CardElementConfiguration
        const parsedInput =
          typeof input === 'string' ? JSON.parse(input) : input;

        onElementConfigurationChange(element, parsedInput);
      },
    };
  }, [accountCard]);

  return {
    onElementConfigurationChange,
    onCardElementConfigurationDelete,
    cardElementLoading,
  };
};

export default useCardElementConfiguration;
